import _ from 'lodash'
import Heading from '@tiptap/extension-heading'
import Blockquote from '@tiptap/extension-blockquote'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { ListItem } from '@tiptap/extension-list-item'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { StarterKit } from '@tiptap/starter-kit'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { Editor } from '@tiptap/react'
import {
  Extension,
  type JSONContent,
  type MarkdownRendererHelpers,
  type MarkdownToken,
  type RenderContext,
} from '@tiptap/core'
import { EditorState, Transaction } from 'prosemirror-state'
import { Fragment, Slice } from 'prosemirror-model'
import { common, createLowlight } from 'lowlight'
import { Markdown } from '@tiptap/markdown'
import { StyleType } from '../../../../utils/markdown'
import { ActiveCellHighlight } from './TableInteractions/activeCellHighlightExtension'

const lowlight = createLowlight(common)

export const getEditorExtensions = () => {
  return [
    StarterKit.configure({
      codeBlock: false,
      heading: false,
      blockquote: false,
      horizontalRule: false,
      paragraph: false,
      listItem: false,
    }),
    CustomParagraph,
    CustomListItem,
    CustomHeading,
    CustomBlockquote,
    Link,
    Image.configure({
      inline: true,
    }),
    CustomCodeBlock.configure({
      lowlight,
    }),
    CustomHorizontalRule,
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    CustomTable,
    TableCell,
    TableHeader,
    TableRow,
    ActiveCellHighlight,
    MarkdownEscape,
    Markdown.configure({
      markedOptions: {
        breaks: true,
      },
    }),
  ]
}

// Markdownソース編集用の最小構成。装飾系を無効化してプレーンテキスト扱いにする。
// @tiptap/markdown は含めず、editor.getText() で生マークダウン文字列として取り出す。
export const getMarkdownSourceExtensions = () => {
  return [
    StarterKit.configure({
      blockquote: false,
      bold: false,
      bulletList: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      italic: false,
      link: false,
      listItem: false,
      listKeymap: false,
      orderedList: false,
      strike: false,
      underline: false,
    }),
  ]
}

// コードフェンス（``` または ~~~）の開始行にマッチ
const CODE_FENCE_RE = /^(`{3,}|~{3,})/

// ソースエディタの空段落非表示CSS回避用。コードブロック内の空行に入れて
// <p><br></p> にならないようにし、CSSの対象外にする。
const ZERO_WIDTH_SPACE = '​'

// ソースエディタ用ペーストハンドラ。
// tiptap デフォルトのペーストは空行を段落区切りとして消すため、
// markdownToSourceDoc と同じロジックで各行を段落ノードに変換し空行を保持する。
export const handleSourceEditorPaste = (
  view: import('prosemirror-view').EditorView,
  event: ClipboardEvent
): boolean => {
  const text = event.clipboardData?.getData('text/plain')
  if (!text || !text.includes('\n')) return false

  const { schema } = view.state
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  let inCodeBlock = false

  const nodes = lines.map(line => {
    if (CODE_FENCE_RE.test(line)) {
      inCodeBlock = !inCodeBlock
    }
    if (line.length > 0) {
      return schema.nodes.paragraph.create(null, [schema.text(line)])
    }
    if (inCodeBlock) {
      return schema.nodes.paragraph.create(null, [
        schema.text(ZERO_WIDTH_SPACE),
      ])
    }
    return schema.nodes.paragraph.create()
  })

  const fragment = Fragment.from(nodes)
  const slice = new Slice(fragment, 1, 1)
  view.dispatch(view.state.tr.replaceSelection(slice))
  return true
}

// Markdown文字列を、Markdownソースエディタ用のdocノードに変換する。
// 改行ごとに段落を作ることで、getText({ blockSeparator: '\n' }) で元の文字列に戻せる。
export const markdownToSourceDoc = (markdown: string) => {
  const lines = markdown.split('\n')
  let inCodeBlock = false
  return {
    type: 'doc',
    content: lines.map(line => {
      if (CODE_FENCE_RE.test(line)) {
        inCodeBlock = !inCodeBlock
      }
      if (line.length > 0) {
        return { type: 'paragraph', content: [{ type: 'text', text: line }] }
      }
      if (inCodeBlock) {
        return {
          type: 'paragraph',
          content: [{ type: 'text', text: ZERO_WIDTH_SPACE }],
        }
      }
      return { type: 'paragraph' }
    }),
  }
}

// Markdown ブロック要素の開始/終了行にマッチ。
const BLOCK_ELEMENT_RE =
  /^(`{3,}|~{3,}|#{1,6}\s|\s*[-*+]\s|\s*\d+\.\s|>\s?|\|)|^(---+|___+|\*\*\*+)\s*$/

const findNearestNonEmptyLine = (
  lines: string[],
  from: number,
  direction: -1 | 1
): string => {
  let i = from + direction
  while (i >= 0 && i < lines.length) {
    if (lines[i] !== '') return lines[i]
    i += direction
  }
  return ''
}

// Markdownソースエディタから、生のMarkdown文字列を取り出す。
// 空行を \xA0 マーカーに変換し、リッチテキストへの切替時に空段落として保持する。
// ブロック要素境界では1行を構造的区切り（Markdown構文上必要）として残し、
// 残りを \xA0 に変換する。そのため N 空行 → N-1 個の可視空行になる。
export const getMarkdownFromSourceEditor = (editor: Editor): string => {
  const text = editor.getText({ blockSeparator: '\n' }).replace(/​/g, '')
  const lines = text.split('\n')
  const result = [...lines]
  let inCodeBlock = false

  let i = 0
  while (i < lines.length) {
    if (CODE_FENCE_RE.test(lines[i])) {
      inCodeBlock = !inCodeBlock
    }
    if (inCodeBlock || lines[i] !== '') {
      i++
      continue
    }

    const runStart = i
    while (i < lines.length && lines[i] === '') {
      i++
    }
    const runEnd = i

    const prev = findNearestNonEmptyLine(lines, runStart, -1)
    const next = findNearestNonEmptyLine(lines, runEnd - 1, 1)
    const nearBlock = BLOCK_ELEMENT_RE.test(prev) || BLOCK_ELEMENT_RE.test(next)

    if (nearBlock) {
      for (let j = runStart; j < runEnd - 1; j++) {
        result[j] = '\xA0'
      }
    } else {
      for (let j = runStart; j < runEnd; j++) {
        result[j] = '\xA0'
      }
    }
  }

  return result.join('\n')
}

// リッチエディタから Markdown を取り出す。
// 先頭がテーブルのとき getMarkdown() が出力先頭に余分な改行を付けるため、
// 先頭の改行を除去して前要素とのズレ・空行混入を防ぐ。
export const getMarkdownFromRichEditor = (editor: Editor): string => {
  return editor.getMarkdown().replace(/^\n+/, '')
}

// marked は `\*` `\_` `\#` `` \` `` 等のバックスラッシュエスケープを escape トークンに分解するが、
// @tiptap/markdown の parseInlineTokens は escape トークンのハンドラを持たず、
// マーク扱いでも子トークンでもないため黙って破棄する。
// escape トークンを「エスケープ後の文字」のテキストノードとして拾い、文字の消失を防ぐ。
const MarkdownEscape = Extension.create({
  name: 'markdownEscape',
  markdownTokenName: 'escape',
  parseMarkdown: (token: MarkdownToken) => ({
    type: 'text',
    text: token.text || '',
  }),
})

// --- テーブルセル内改行の保持 ---
// 既定のテーブルシリアライザは collapseWhitespace(/\s+/ → " ") でセル内容を1行に潰すため、
// hardBreak（  \n）や段落境界が消失する。
// テキストに "<br>" を直接入れると encodeHtmlEntities で "&lt;br&gt;" にされるため、
// 私用領域のセンチネルへ置換してからシリアライズし、出力後に <br> へ復元する。

const TABLE_BR_SENTINEL = String.fromCharCode(0xe000, 0xe001, 0xe002, 0xe003)

const replaceHardBreaks = (node: JSONContent): JSONContent => {
  if (node.type === 'hardBreak') {
    return { type: 'text', text: TABLE_BR_SENTINEL }
  }
  if (!node.content) return node
  return { ...node, content: node.content.map(replaceHardBreaks) }
}

const flattenTableCellLineBreaks = (cell: JSONContent): JSONContent => {
  const blocks = (cell.content ?? []).map(replaceHardBreaks)
  if (blocks.length <= 1) return { ...cell, content: blocks }
  const inline: JSONContent[] = []
  blocks.forEach((block, index) => {
    if (index > 0) {
      inline.push({ type: 'text', text: TABLE_BR_SENTINEL })
    }
    inline.push(...(block.content ?? []))
  })
  return { ...cell, content: [{ type: 'paragraph', content: inline }] }
}

const normalizeTableCellLineBreaks = (node: JSONContent): JSONContent => {
  if (node.type === 'tableCell' || node.type === 'tableHeader') {
    return flattenTableCellLineBreaks(node)
  }
  if (!node.content) return node
  return { ...node, content: node.content.map(normalizeTableCellLineBreaks) }
}

const baseTableRenderMarkdown = Table.config.renderMarkdown

const CustomTable = Table.extend({
  renderMarkdown(
    node: JSONContent,
    helpers: MarkdownRendererHelpers,
    ctx: RenderContext
  ) {
    const rendered =
      baseTableRenderMarkdown?.(
        normalizeTableCellLineBreaks(node),
        helpers,
        ctx
      ) || ''
    return rendered.split(TABLE_BR_SENTINEL).join('<br>')
  },
})

const EMPTY_PARAGRAPH_MARKDOWN = '&nbsp;'

// stock の renderMarkdown は「2つ目以降の空段落」だけ &nbsp; を出力し、
// 1つ目は空文字に頼る。しかし parse 側は separatorCount-1 で計算するため
// 空行だけでは1つ目の空段落が消失する。常に &nbsp; を出力して往復を保証する。
const CustomParagraph = Paragraph.extend({
  renderMarkdown: (node, h) => {
    if (!node) return ''
    const content = Array.isArray(node.content) ? node.content : []
    if (content.length === 0 || content.every(n => n.type === 'hardBreak')) {
      return EMPTY_PARAGRAPH_MARKDOWN
    }
    return h.renderChildren(content)
  },
})

// stock の renderNestedMarkdownContent は hardBreak（  \n）後の継続行に
// リスト項目プレフィックス分のインデントを付与しない。
// そのため Markdown パーサーが継続行をリスト項目の外と解釈し、番号が消費される。
const CustomListItem = ListItem.extend({
  renderMarkdown: (node, h, ctx) => {
    if (!node || !Array.isArray(node.content)) return ''

    let prefix: string
    if (ctx?.parentType === 'orderedList') {
      const start = ctx.meta?.parentAttrs?.start || 1
      prefix = `${start + ctx.index}. `
    } else {
      prefix = '- '
    }

    const [content, ...children] = node.content
    const mainContent = h.renderChildren([content])

    const mainLines = mainContent.split('\n')
    const continuationIndent = ' '.repeat(prefix.length)
    let output = prefix + mainLines[0]
    for (let i = 1; i < mainLines.length; i++) {
      output += '\n' + (mainLines[i] ? continuationIndent + mainLines[i] : '')
    }

    children.forEach((child, i) => {
      const childContent =
        h.renderChild?.(child, i + 1) ?? h.renderChildren([child])
      if (childContent != null) {
        const indentedChild = childContent
          .split('\n')
          .map(line => h.indent(line))
          .join('\n')
        output +=
          child.type === 'paragraph'
            ? `\n\n${indentedChild}`
            : `\n${indentedChild}`
      }
    })

    return output
  },
})

const CustomCodeBlock = CodeBlockLowlight.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => this.editor.commands.toggleCodeBlock(),
    }
  },
})

const CustomBlockquote = Blockquote.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-.': () => this.editor.commands.toggleBlockquote(),
    }
  },
})

const CustomHeading = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-1': () => this.editor.commands.toggleHeading({ level: 1 }),
      'Mod-Shift-2': () => this.editor.commands.toggleHeading({ level: 2 }),
      'Mod-Shift-3': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Shift-4': () => this.editor.commands.toggleHeading({ level: 4 }),
      'Mod-Shift-5': () => this.editor.commands.toggleHeading({ level: 5 }),
      'Mod-Shift-6': () => this.editor.commands.toggleHeading({ level: 6 }),
    }
  },
})

const CustomHorizontalRule = HorizontalRule.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-h': () => this.editor.commands.setHorizontalRule(),
    }
  },
})

export const splitHardBreaks = ({
  tr,
  state,
}: {
  tr: Transaction
  state: EditorState
}): boolean => {
  const { from, to } = state.selection
  const hardBreakPositions: number[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'hardBreak') {
      hardBreakPositions.push(pos)
    }
  })

  for (let i = hardBreakPositions.length - 1; i >= 0; i--) {
    const pos = hardBreakPositions[i]
    tr.delete(pos, pos + 1)
    tr.split(pos)
  }

  return true
}

export const findHighLightItemsInRichText = (editor: Editor): string[] => {
  let items: string[] = []
  if (editor.isActive('heading', { level: 1 })) {
    items = [...items, StyleType.HEADER_ONE]
  }
  if (editor.isActive('heading', { level: 2 })) {
    items = [...items, StyleType.HEADER_TWO]
  }
  if (editor.isActive('heading', { level: 3 })) {
    items = [...items, StyleType.HEADER_THREE]
  }
  if (editor.isActive('heading', { level: 4 })) {
    items = [...items, StyleType.HEADER_FOUR]
  }
  if (editor.isActive('heading', { level: 5 })) {
    items = [...items, StyleType.HEADER_FIVE]
  }
  if (editor.isActive('heading', { level: 6 })) {
    items = [...items, StyleType.HEADER_SIX]
  }
  if (editor.isActive('bold')) items = [...items, StyleType.BOLD]
  if (editor.isActive('italic')) items = [...items, StyleType.ITALIC]
  if (editor.isActive('strike')) items = [...items, StyleType.STRIKETHROUGH]
  if (editor.isActive('code')) items = [...items, StyleType.CODE]
  if (editor.isActive('codeBlock')) items = [...items, StyleType.CODE_BLOCK]
  if (editor.isActive('blockquote')) items = [...items, StyleType.BLOCKQUOTE]
  if (editor.isActive('bulletList')) {
    items = [...items, StyleType.UNORDERED_LIST_ITEM]
  }
  if (editor.isActive('orderedList')) {
    items = [...items, StyleType.ORDERED_LIST_ITEM]
  }
  if (editor.isActive('horizontalRule')) {
    items = [...items, StyleType.HORIZONTAL_RULE]
  }
  if (editor.isActive('taskList')) items = [...items, StyleType.TASK_LIST]
  if (editor.isActive('table')) items = [...items, StyleType.TABLE]
  if (editor.isActive('image')) items = [...items, StyleType.FILE]
  return items
}

const CLIPBOARD_DATA_TYPE_HTML: string = 'text/html'
const HTML_ANCHOR_TAG_REGEX: string = '<a '

export const insertLinkFromClipboard = (
  clipboardData: DataTransfer,
  editor: Editor
): boolean => {
  if (
    !clipboardData ||
    clipboardData.types.indexOf(CLIPBOARD_DATA_TYPE_HTML) < 0
  ) {
    return false
  }

  const html: string = clipboardData.getData(CLIPBOARD_DATA_TYPE_HTML)
  if (
    _.isEmpty(html) ||
    !new RegExp(HTML_ANCHOR_TAG_REGEX).test(html.replaceAll('\r\n', ''))
  ) {
    return false
  }
  // ProseMirror エディタからのペーストは通常の paste handler に任せる。
  // data-pm-slice があるペーストを横取りすると、構造復元やtransformPastedHTML が
  // バイパスされ、コードブロック等の構造が壊れる。
  if (html.includes('data-pm-slice')) {
    return false
  }
  editor.commands.insertContent(html, {
    parseOptions: {
      preserveWhitespace: false,
    },
  })
  return true
}
